import ngmodule from "./ngmodule";
import { IWebServiceUrl } from "../common/web-service-url";
import { ODataBuilder, ODataArgs, ODataResponse } from "./odata-builder";

export type getFacilityStructuresResult = {
	findBySiteId: (siteId: string) => Record | undefined;
	children: Record[],
	facilities: FacilityRecord[],
	buildings: BuildingRecord[],
	floors: FloorRecord[],
	zones: ZoneRecord[],
	sites: SiteRecord[],
	byType: [FacilityRecord[], BuildingRecord[], FloorRecord[], ZoneRecord[], SiteRecord[]]
}

export interface IFacilityStructureHelper {
	getFacilityStructures(force?: boolean): ng.IPromise<getFacilityStructuresResult>;
}

export const ShowFacilityStructureTypes = {
	None: -2,
	Smart: -1,
	Facility: 0,
	Building: 1,
	Floor: 2,
	Zone: 3,
	Site: 4
};

export type ODataRecord = {
	StructureType: number;
	Id: string;
	ParentId: string;
	Name: string;
	Level: number;
	SiteId: string;
	Archived: boolean;
}

export type Record = ODataRecord & {
	parent: Record;
	children: Record[];
}

export type FacilityRecord = Record & {
	buildings: BuildingRecord[];
}
export type BuildingRecord = Record & {
	floors: FloorRecord[];
	facility: FacilityRecord;
}
export type FloorRecord = Record & {
	zones: ZoneRecord[];
	building: BuildingRecord;
}
export type ZoneRecord = Record & {
	sites: SiteRecord[];
	floor: FloorRecord;
}
export type SiteRecord = Record & {
	zone: ZoneRecord;
}

class FacilityStructureHelper implements IFacilityStructureHelper {
	static $inject = ['$http', '$q', 'webServiceUrl'];

	constructor(private $http: ng.IHttpService, private $q: ng.IQService, private webServiceUrl: IWebServiceUrl) { }

	private $facilityStructures: ng.IPromise<getFacilityStructuresResult> | null = null;

	getFacilityStructures(force?: boolean): ng.IPromise<getFacilityStructuresResult> {
		const feedArgs: ODataArgs<ODataRecord> = {
			feed: 'FacilityStructures',
			filter: 'Archived eq false',
			select: ['Id', 'ParentId', 'StructureType', 'Name', 'Level', 'SiteId'],
			orderBy: ['StructureType', 'Level'],
		};
		let fsOData = new ODataBuilder<ODataRecord>(this.webServiceUrl.odata(), feedArgs);

		if (this.$facilityStructures == null || force) {
			this.$facilityStructures = this.$http.get<ODataResponse<ODataRecord>>(fsOData.build()).then(function (response) {
				let all = response.data.value as Record[];
				let facilities = all.filter(a => a.StructureType == ShowFacilityStructureTypes.Facility) as FacilityRecord[];
				let buildings = all.filter(a => a.StructureType == ShowFacilityStructureTypes.Building) as BuildingRecord[];
				let floors = all.filter(a => a.StructureType == ShowFacilityStructureTypes.Floor) as FloorRecord[];
				let zones = all.filter(a => a.StructureType == ShowFacilityStructureTypes.Zone) as ZoneRecord[];
				let sites = all.filter(a => a.StructureType == ShowFacilityStructureTypes.Site) as SiteRecord[];

				facilities.forEach(facility => {
					facility.buildings = facility.children = buildings.filter(building => building.ParentId == facility.Id);
				});

				buildings.forEach((building: any) => {
					building.floors = building.children = floors.filter(floor => floor.ParentId == building.Id);
					building.facility = facilities.find(facility => facility.Id == building.ParentId);
					building.parent = building.facility;
				});

				floors.forEach((floor: any) => {
					floor.zones = floor.children = zones.filter(zone => zone.ParentId == floor.Id);
					floor.building = buildings.find(building => building.Id == floor.ParentId);
					floor.parent = floor.building != null ? (floor.building.parent != null ? floor.building : null) : null;
				});

				zones.forEach((zone: any) => {
					zone.sites = zone.children = sites.filter(site => site.ParentId == zone.Id);
					zone.floor = floors.find(floor => floor.Id == zone.ParentId);
					zone.parent = zone.floor != null ? (zone.floor.parent != null ? zone.floor : null) : null;
				});

				sites.forEach((site: any) => {
					site.zone = zones.find(zone => zone.Id == site.ParentId);
					site.parent = site.zone != null ? (site.zone.parent != null ? site.zone : null) : null;
				});


				// Tidy up, all elements must have a parent (except a Facility)
				all = all.filter(a => a.StructureType == ShowFacilityStructureTypes.Facility || a.parent != null);
				buildings = buildings.filter(a => a.parent != null);
				floors = floors.filter(a => a.parent != null);
				zones = zones.filter(a => a.parent != null);
				sites = sites.filter(a => a.parent != null);

				return {
					findBySiteId: function (siteId: string) {
						return all.find(fs => fs.SiteId == siteId);
					},
					children: facilities,
					facilities: facilities,
					buildings: buildings,
					floors: floors,
					zones: zones,
					sites: sites,
					byType: [facilities, buildings, floors, zones, sites]
				}
			});
		}
		return this.$facilityStructures;
	}
}

ngmodule.service('facilityStructureHelper', FacilityStructureHelper);


